type Listener = (hasUnread: boolean) => void

const STORAGE_KEY = "cityChat:lastReadAt"

let hasUnread = false
let lastReadAtMs = 0
let viewingChat = false
let storageReady = false
const listeners = new Set<Listener>()

function emit() {
  for (const listener of listeners) listener(hasUnread)
}

function setHasUnread(next: boolean) {
  if (next === hasUnread) return
  hasUnread = next
  emit()
}

function ensureStorageLoaded(): void {
  if (storageReady) return
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? Number(raw) : 0
    if (Number.isFinite(parsed) && parsed > 0) {
      lastReadAtMs = parsed
    } else {
      lastReadAtMs = Date.now()
      localStorage.setItem(STORAGE_KEY, String(lastReadAtMs))
    }
  } catch {
    lastReadAtMs = Date.now()
  }
  storageReady = true
}

export function getCityChatHasUnread(): boolean {
  return hasUnread
}

export function subscribeCityChatUnread(listener: Listener): () => void {
  listeners.add(listener)
  ensureStorageLoaded()
  listener(hasUnread)
  return () => listeners.delete(listener)
}

export function setCityChatViewing(viewing: boolean): void {
  ensureStorageLoaded()
  viewingChat = viewing
  if (viewing) setHasUnread(false)
}

export async function markCityChatRead(uptoMs?: number): Promise<void> {
  ensureStorageLoaded()
  const next = Math.max(lastReadAtMs, uptoMs ?? Date.now(), Date.now())
  if (next !== lastReadAtMs) {
    lastReadAtMs = next
    try {
      localStorage.setItem(STORAGE_KEY, String(lastReadAtMs))
    } catch {
      /* ignore */
    }
  }
  setHasUnread(false)
}

function messageTimeMs(createdAt?: string | null): number {
  if (!createdAt) return 0
  const t = Date.parse(createdAt)
  return Number.isFinite(t) ? t : 0
}

export async function noteCityChatIncoming(params: {
  createdAt?: string | null
  authorUserId?: number | null
  selfUserId?: number | null
}): Promise<void> {
  ensureStorageLoaded()
  if (viewingChat) return
  const { authorUserId: authorId, selfUserId: selfId } = params
  if (
    authorId != null &&
    selfId != null &&
    Number.isFinite(authorId) &&
    Number.isFinite(selfId) &&
    authorId === selfId
  ) {
    return
  }
  const t = messageTimeMs(params.createdAt)
  if (t > 0 && t <= lastReadAtMs) return
  setHasUnread(true)
}

export async function noteCityChatHistory(params: {
  messages: Array<{ createdAt?: string; author?: { userId?: number } }>
  selfUserId?: number | null
}): Promise<void> {
  ensureStorageLoaded()
  if (viewingChat) {
    const maxMs = params.messages.reduce(
      (acc, m) => Math.max(acc, messageTimeMs(m.createdAt)),
      0
    )
    if (maxMs > 0) await markCityChatRead(maxMs)
    return
  }
  const selfId = params.selfUserId
  const hasNew = params.messages.some((m) => {
    const t = messageTimeMs(m.createdAt)
    if (t <= lastReadAtMs) return false
    const authorId = m.author?.userId
    if (
      authorId != null &&
      selfId != null &&
      Number.isFinite(authorId) &&
      Number.isFinite(selfId) &&
      authorId === selfId
    ) {
      return false
    }
    return true
  })
  if (hasNew) setHasUnread(true)
}

export function resetCityChatUnreadForLogout(): void {
  hasUnread = false
  viewingChat = false
  emit()
}
